// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';

import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useAsyncError from 'Hooks/useAsyncError';
import useAnalytics from 'Hooks/useAnalytics';
import download from 'Utils/download';
import AccountStatementsComponent from 'Components/Reports/AccountStatements';
import { API_ACCOUNT_STATEMENTS } from 'Constants';

const POLLING_INTERVAL = 10000;
const POLLING_TIMEOUT = 10 * 60 * 1000;
const ACTIVE_STATUSES = ['PENDING', 'PROCESSING'];

const mapStatement = statement => ({
  ...statement,
  id: (statement?.id || statement?.statementId || statement?.accountStatementId)?.toString(),
  period: {
    startDate: statement?.startDate,
    endDate: statement?.endDate,
    label: statement?.period,
  },
  requestedAt: statement?.requestedAt || statement?.createdAt,
  actions: {
    id: statement?.id || statement?.statementId || statement?.accountStatementId,
    status: statement?.status,
  }
});

const getStatementsList = res => {
  if (Array.isArray(res)) {
    return res;
  }

  if (Array.isArray(res?.accountStatements)) {
    return res.accountStatements;
  }

  if (Array.isArray(res?.statements)) {
    return res.statements;
  }

  if (Array.isArray(res?.items)) {
    return res.items;
  }

  if (Array.isArray(res?.data)) {
    return res.data;
  }

  if (Array.isArray(res?.accountStatements?.data)) {
    return res.accountStatements.data;
  }

  if (Array.isArray(res?.accountStatements?.items)) {
    return res.accountStatements.items;
  }

  return [];
};

const getPaginationData = res => {
  const pagination = res?.pagination || res?.meta || (
    res?.accountStatements && !Array.isArray(res.accountStatements) ? res.accountStatements : res
  );

  return {
    currentPage: pagination?.currentPage || pagination?.current_page,
    recordsPerPage: pagination?.recordsPerPage || pagination?.perPage || pagination?.per_page,
    totalPages: pagination?.totalPages || pagination?.lastPage || pagination?.last_page,
    totalRecords: pagination?.totalRecords || pagination?.total,
  };
};

const getErrorMessage = (err, fallback) => {
  if (Array.isArray(err?.info?.errors) && err.info.errors.length) {
    return [...new Set(err.info.errors.map(item => item?.message).filter(Boolean))].join('\r\n');
  }

  return err?.info?.message || err.message || fallback;
};

const AccountStatements = memo(() => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [errors, setErrors] = useState([]);
  const fetcher = useFetch();
  const { t } = useTranslation();
  const throwError = useAsyncError();
  const { data: { email, loggedIn } = {} } = useUser();
  const { query, location } = useRouter();
  const analytics = useAnalytics();
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);

  const hasActiveStatement = statements.some(statement => ACTIVE_STATUSES.includes(statement?.status));

  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  const getStatements = useCallback(({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    let path = API_ACCOUNT_STATEMENTS;

    if (query) {
      path += `?${ stringify({ ...query }) }`;
    }

    return fetcher(path, { noCancel: true }).then(res => {
      const rawStatements = getStatementsList(res);
      const resPagination = getPaginationData(res);

      if (!rawStatements.length) {
        setStatements([]);
        setPagination(resPagination);
        return [];
      }

      const mappedStatements = rawStatements.map(mapStatement);

      setStatements(mappedStatements);
      setPagination(resPagination);

      return mappedStatements;
    }).catch(err => {
      throwError(err);
      return [];
    })
      .finally(() => {
        if (!silent) {
          setLoading(false);
        }
      });
  }, [query, fetcher, throwError]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      return;
    }

    const poll = () => {
      getStatements({ silent: true }).then(nextStatements => {
        const hasPendingStatement = nextStatements.some(statement => ACTIVE_STATUSES.includes(statement?.status));

        if (!hasPendingStatement) {
          clearPolling();
        }
      });
    };

    pollingIntervalRef.current = setInterval(poll, POLLING_INTERVAL);
    pollingTimeoutRef.current = setTimeout(clearPolling, POLLING_TIMEOUT);
  }, [clearPolling, getStatements]);

  useEffect(() => {
    if (loggedIn) {
      getStatements();
    }
  }, [location.pathname, query, email, loggedIn, getStatements]);

  useEffect(() => {
    if (hasActiveStatement) {
      startPolling();
    } else {
      clearPolling();
    }
  }, [hasActiveStatement, startPolling, clearPolling]);

  useEffect(() => clearPolling, [clearPolling]);

  const handleRequestStatement = ({ startDate, endDate }) => {
    analytics.track('request_account_statement', { startDate, endDate });
    setRequestLoading(true);
    setErrors([]);

    return fetcher(API_ACCOUNT_STATEMENTS, {
      method: 'POST',
      body: JSON.stringify({ startDate, endDate })
    }).then(() => {
      getStatements({ silent: true });
      startPolling();
      return true;
    }).catch(err => {
      setErrors([{ type: 'error', message: getErrorMessage(err, t('reports.accountStatements.requestError')) }]);
      return false;
    }).finally(() => setRequestLoading(false));
  };

  const handleDownload = statementId => {
    analytics.track('file_download', { type: 'account_statement_csv' });
    download(`${ API_ACCOUNT_STATEMENTS }/${ statementId }/file`).catch(err => {
      setErrors([{ type: 'error', message: getErrorMessage(err, t('reports.accountStatements.downloadError')) }]);
    });
  };

  return (
    <AccountStatementsComponent
      statements={ statements }
      loading={ loading }
      requestLoading={ requestLoading }
      pagination={ pagination }
      errors={ errors }
      hasActiveStatement={ hasActiveStatement }
      onRequestStatement={ handleRequestStatement }
      onDownload={ handleDownload }
    />
  );
});

AccountStatements.displayName = 'AccountStatementsContainer';

export default AccountStatements;
